import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { countryOf } from '../../utils/dialCountry';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CallReceived as InboundIcon,
  PhoneCallback as OutboundIcon,
  CallEnd as HangupIcon,
  BugReport as TraceIcon,
  Monitor as MonitorIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Router as KamailioIcon,
  Cable as RTPIcon,
  PhoneForwarded as FSIcon,
  NetworkCheck as NetworkIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface ActiveCall {
  id: number;
  call_id: string;
  sip_call_id: string;
  direction: 'inbound' | 'outbound';
  src_number: string;
  dst_number: string;
  status: 'ringing' | 'connected' | 'on_hold';
  answer_time: string | null;
  start_time: string;
  tenant_name: string;
  carrier_name: string;
}

interface CallStats {
  total: number;
  ringing: number;
  connected: number;
  on_hold: number;
}

interface ServerMetrics {
  timestamp: string;
  cpu: { pct: number; cores: number; processes: number };
  load: { load1: number; load5: number; load15: number };
  memory: { total_kb: number; used_kb: number; pct: number };
  disk: { total_mb: number; used_mb: number; pct: number };
  network: { rx_bytes: number; tx_bytes: number };
  uptime: { seconds: number };
  kamailio: { active_dialogs: number; tx_count: number; status: string };
  rtpengine: { sessions: number; status: string };
  freeswitch: { active_calls: number; status: string };
}

interface SipMsg {
  t: number; dir: string; proto: string; src: string; dst: string;
  src_host: string; dst_host: string; kind: 'req' | 'resp'; code: number;
  label: string; cseq: string; raw: string;
}
interface SipTrace {
  call_id: string;
  count?: number;
  hosts?: string[];
  messages?: SipMsg[];
  trace: string;
  captured_at: string;
}

const formatDuration = (startTime: string, answerTime: string | null): string => {
  const base = answerTime || startTime;
  const seconds = Math.floor((Date.now() - new Date(base).getTime()) / 1000);
  if (seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatBytes = (bytes: number): string => {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const formatUptime = (seconds: number): string => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
};

const formatMemory = (usedKb: number, totalKb: number, pct: number): string => {
  const usedGb = (usedKb / 1048576).toFixed(1);
  const totalGb = (totalKb / 1048576).toFixed(1);
  return `${usedGb} GB / ${totalGb} GB (${pct}%)`;
};

const getProgressColor = (pct: number): 'success' | 'warning' | 'error' => {
  if (pct >= 80) return 'error';
  if (pct >= 60) return 'warning';
  return 'success';
};

const statusColors: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  connected: 'success',
  ringing: 'warning',
  on_hold: 'info',
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const isUp = status === 'up' || status === 'running' || status === 'active';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: isUp ? '#00e676' : status === 'unknown' ? '#9e9e9e' : '#f44336',
        boxShadow: isUp ? '0 0 6px #00e676' : 'none',
        animation: isUp ? 'pulse 2s ease-in-out infinite' : 'none',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.4 },
        },
      }}
    />
  );
};

const SBCMonitorPanel: React.FC<{ onClose?: () => void }> = () => {
  const [metrics, setMetrics] = useState<ServerMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMetrics = useCallback(async () => {
    setSpinning(true);
    try {
      const res = await api.get<ServerMetrics>('/calls/server-metrics');
      setMetrics(res.data);
    } catch {
      // keep stale data on error
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    intervalRef.current = setInterval(fetchMetrics, 3000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchMetrics]);

  const darkCard = {
    bgcolor: '#0d1117',
    border: '1px solid #1e2a3a',
    borderRadius: 1,
    p: 1.5,
    mb: 1.5,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 380,
        flexShrink: 0,
        bgcolor: '#0a0e1a',
        border: '1px solid #1e2a3a',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonitorIcon sx={{ color: '#00b0ff', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ color: '#e0e0e0', fontWeight: 600, letterSpacing: 0.5 }}>
            SBC Monitor
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {metrics && (
            <Typography variant="caption" sx={{ color: '#607d8b' }}>
              {new Date(metrics.timestamp).toLocaleTimeString()}
            </Typography>
          )}
          <RefreshIcon
            sx={{
              color: '#607d8b',
              fontSize: 16,
              animation: spinning ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={24} sx={{ color: '#00b0ff' }} />
          </Box>
        ) : metrics ? (
          <>
            <Box sx={darkCard}>
              <Typography variant="caption" sx={{ color: '#00b0ff', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                Server Resources
              </Typography>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#90a4ae' }}>CPU</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 600 }}>{metrics.cpu.pct}%</Typography>
                    <Typography variant="caption" sx={{ color: '#546e7a' }}>{metrics.cpu.cores} cores</Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={metrics.cpu.pct}
                  color={getProgressColor(metrics.cpu.pct)}
                  sx={{ height: 5, borderRadius: 1, bgcolor: '#1e2a3a' }}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#90a4ae' }}>Memory</Typography>
                  <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                    {formatMemory(metrics.memory.used_kb, metrics.memory.total_kb, metrics.memory.pct)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={metrics.memory.pct}
                  color={getProgressColor(metrics.memory.pct)}
                  sx={{ height: 5, borderRadius: 1, bgcolor: '#1e2a3a' }}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#90a4ae' }}>Disk</Typography>
                  <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 600 }}>
                    {metrics.disk.pct}% ({Math.round(metrics.disk.used_mb / 1024)} / {Math.round(metrics.disk.total_mb / 1024)} GB)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={metrics.disk.pct}
                  color={getProgressColor(metrics.disk.pct)}
                  sx={{ height: 5, borderRadius: 1, bgcolor: '#1e2a3a' }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#90a4ae' }}>Load Avg</Typography>
                <Typography variant="caption" sx={{ color: '#e0e0e0', fontFamily: 'monospace' }}>
                  {metrics.load.load1.toFixed(2)} | {metrics.load.load5.toFixed(2)} | {metrics.load.load15.toFixed(2)}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#546e7a', display: 'block', textAlign: 'right', fontSize: '0.65rem' }}>
                1m / 5m / 15m
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#90a4ae' }}>Uptime</Typography>
                <Typography variant="caption" sx={{ color: '#00e676', fontWeight: 600 }}>
                  {formatUptime(metrics.uptime.seconds)}
                </Typography>
              </Box>
            </Box>

            <Box sx={darkCard}>
              <Typography variant="caption" sx={{ color: '#00b0ff', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                SBC Services
              </Typography>

              {[
                {
                  icon: <KamailioIcon sx={{ fontSize: 16, color: '#607d8b' }} />,
                  label: 'Kamailio',
                  status: metrics.kamailio.status,
                  detail: `${metrics.kamailio.active_dialogs} dialogs`,
                  statusLabel: metrics.kamailio.status === 'up' ? 'active' : 'down',
                },
                {
                  icon: <RTPIcon sx={{ fontSize: 16, color: '#607d8b' }} />,
                  label: 'RTPEngine',
                  status: metrics.rtpengine.status,
                  detail: `${metrics.rtpengine.sessions} sessions`,
                  statusLabel: metrics.rtpengine.status === 'up' ? 'active' : 'down',
                },
                {
                  icon: <FSIcon sx={{ fontSize: 16, color: '#607d8b' }} />,
                  label: 'FreeSWITCH',
                  status: metrics.freeswitch.status,
                  detail: `${metrics.freeswitch.active_calls} calls`,
                  statusLabel: metrics.freeswitch.status === 'up' ? 'running' : 'down',
                },
              ].map((svc) => (
                <Box
                  key={svc.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.75,
                    borderBottom: '1px solid #1e2a3a',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {svc.icon}
                    <Typography variant="caption" sx={{ color: '#b0bec5', fontWeight: 500 }}>
                      {svc.label}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#546e7a' }}>{svc.detail}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusDot status={svc.status} />
                      <Typography
                        variant="caption"
                        sx={{ color: svc.status === 'up' || svc.status === 'running' || svc.status === 'active' ? '#00e676' : '#f44336', fontWeight: 600 }}
                      >
                        {svc.statusLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                bgcolor: '#0d1117',
                border: '1px solid #1e2a3a',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <NetworkIcon sx={{ color: '#00b0ff', fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: '#90a4ae', flex: 1 }}>Network</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#546e7a', display: 'block', fontSize: '0.65rem' }}>RX</Typography>
                  <Typography variant="caption" sx={{ color: '#00e676', fontWeight: 600, fontFamily: 'monospace' }}>
                    {formatBytes(metrics.network.rx_bytes)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#546e7a', display: 'block', fontSize: '0.65rem' }}>TX</Typography>
                  <Typography variant="caption" sx={{ color: '#00b0ff', fontWeight: 600, fontFamily: 'monospace' }}>
                    {formatBytes(metrics.network.tx_bytes)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Typography variant="caption" sx={{ color: '#607d8b', display: 'block', textAlign: 'center', pt: 4 }}>
            Failed to load metrics
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const SipLadder: React.FC<{ trace: SipTrace }> = ({ trace }) => {
  const hosts = trace.hosts || [];
  const msgs = trace.messages || [];
  const N = hosts.length || 1;
  const GUT = 66; // px gutter for the timestamp column
  const colorFor = (m: SipMsg) => m.kind === 'resp'
    ? (m.code >= 400 ? '#ff5252' : m.code >= 300 ? '#ffb74d' : m.code >= 200 ? '#00e676' : '#ffd54f')
    : '#40c4ff';
  const center = (i: number) => `calc(${GUT}px + (100% - ${GUT}px) * ${(i + 0.5) / N})`;
  const span = (lo: number, hi: number) => `calc((100% - ${GUT}px) * ${(hi - lo) / N})`;
  return (
    <Box sx={{ position: 'relative', bgcolor: '#050810', maxHeight: 520, overflowY: 'auto', overflowX: 'hidden', pb: 2 }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#0a0e1a', borderBottom: '1px solid #1e2a3a', height: 34 }}>
        {hosts.map((h, i) => (
          <Typography key={i} title={h} sx={{ position: 'absolute', top: 8, left: center(i), transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: '#b0bec5', whiteSpace: 'nowrap' }}>{h}</Typography>
        ))}
      </Box>
      <Box sx={{ position: 'relative', pt: 1 }}>
        {hosts.map((_, i) => (
          <Box key={i} sx={{ position: 'absolute', top: 0, bottom: 0, left: center(i), width: '1px', bgcolor: '#16202e' }} />
        ))}
        {msgs.map((m, idx) => {
          const si = hosts.indexOf(m.src_host), di = hosts.indexOf(m.dst_host);
          if (si < 0 || di < 0 || si === di) return null;
          const lo = Math.min(si, di), hi = Math.max(si, di);
          const right = di > si;
          const color = colorFor(m);
          const ts = new Date(m.t * 1000).toISOString().substr(11, 12);
          const cseqNum = (m.cseq || '').split(' ')[0];
          return (
            <Box key={idx} sx={{ position: 'relative', height: 42 }}>
              <Typography sx={{ position: 'absolute', left: 0, top: 16, width: GUT - 8, textAlign: 'right', fontSize: 10, color: '#546e7a', fontFamily: 'monospace' }}>{ts}</Typography>
              <Typography sx={{ position: 'absolute', top: 4, left: center(lo), width: span(lo, hi), textAlign: 'center', fontSize: 11, fontWeight: 600, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.label}{cseqNum ? `  ·  ${cseqNum}` : ''}
              </Typography>
              <Box sx={{ position: 'absolute', top: 24, left: center(lo), width: span(lo, hi), height: 0, borderTop: `2px ${m.kind === 'resp' ? 'dashed' : 'solid'} ${color}` }}>
                <Box sx={{ position: 'absolute', top: -6, [right ? 'right' : 'left']: -3, color, fontSize: 11, lineHeight: 1 }}>{right ? '▶' : '◀'}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const SipTraceModal: React.FC<{ callId: string | null; open: boolean; onClose: () => void }> = ({ callId, open, onClose }) => {
  const [trace, setTrace] = useState<SipTrace | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rawView, setRawView] = useState(false);

  const fetchTrace = useCallback(async () => {
    if (!callId) return;
    setLoading(true);
    setTrace(null);
    try {
      const res = await api.get<SipTrace>(`/calls/sip-trace`, { params: { call_id: callId } });
      setTrace(res.data);
    } catch {
      setTrace({ call_id: callId, trace: 'Failed to capture SIP trace. The call may have ended.', captured_at: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    if (open && callId) fetchTrace();
  }, [open, callId, fetchTrace]);

  const handleCopy = () => {
    if (trace?.trace) {
      navigator.clipboard.writeText(trace.trace);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#0d1117', border: '1px solid #1e2a3a' } }}>
      <DialogTitle sx={{ color: '#e0e0e0', borderBottom: '1px solid #1e2a3a', pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TraceIcon sx={{ color: '#00b0ff', fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            SIP Trace — {callId}
          </Typography>
          {trace && (
            <Typography variant="caption" sx={{ color: '#607d8b' }}>
              Captured at {new Date(trace.captured_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, py: 1, bgcolor: '#0a0e1a', borderBottom: '1px solid #1e2a3a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#607d8b', fontStyle: 'italic', flex: 1 }}>
            {trace?.count ? `${trace.count} SIP messages across ${trace.hosts?.length || 0} endpoints` : 'SIP ladder from Kamailio capture'}
          </Typography>
          <Button size="small" onClick={() => setRawView(v => !v)} sx={{ color: '#90a4ae', fontSize: 11, minWidth: 0 }}>
            {rawView ? 'Ladder view' : 'Raw view'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#00b0ff', mb: 1 }} />
              <Typography variant="caption" sx={{ color: '#607d8b', display: 'block' }}>Loading SIP trace...</Typography>
            </Box>
          </Box>
        ) : !trace ? null : (!rawView && trace.messages && trace.messages.length > 0) ? (
          <SipLadder trace={trace} />
        ) : (
          <Box component="pre" sx={{ m: 0, p: 2, fontFamily: 'monospace', fontSize: '0.75rem', color: '#00e676', bgcolor: '#050810', maxHeight: 500, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>
            {trace.trace}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e2a3a', px: 2, py: 1.5, gap: 1 }}>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchTrace}
          disabled={loading}
          sx={{ color: '#00b0ff', borderColor: '#1e2a3a', '&:hover': { borderColor: '#00b0ff' } }}
          variant="outlined"
        >
          Re-capture
        </Button>
        <Button
          size="small"
          startIcon={<CopyIcon />}
          onClick={handleCopy}
          disabled={!trace}
          sx={{ color: copied ? '#00e676' : '#90a4ae', borderColor: '#1e2a3a' }}
          variant="outlined"
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} sx={{ color: '#607d8b' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const LiveCallsPage: React.FC = () => {
  const theme = useTheme();
  const [calls, setCalls] = useState<ActiveCall[]>([]);
  const [stats, setStats] = useState<CallStats>({ total: 0, ringing: 0, connected: 0, on_hold: 0 });
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [traceCallId, setTraceCallId] = useState<string | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [hangingUp, setHangingUp] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');

  const callsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCalls = useCallback(async () => {
    try {
      const [callsRes, statsRes] = await Promise.all([
        api.get<ActiveCall[]>('/calls/active'),
        api.get<CallStats>('/calls/stats'),
      ]);
      setCalls(callsRes.data);
      setStats(statsRes.data);
    } catch {
      // retain stale data
    }
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  useEffect(() => {
    if (autoRefresh) {
      callsIntervalRef.current = setInterval(fetchCalls, 5000);
    } else {
      if (callsIntervalRef.current) clearInterval(callsIntervalRef.current);
    }
    return () => {
      if (callsIntervalRef.current) clearInterval(callsIntervalRef.current);
    };
  }, [autoRefresh, fetchCalls]);

  useEffect(() => {
    tickIntervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    };
  }, []);

  const handleHangup = async (call: ActiveCall) => {
    setHangingUp(call.id);
    try {
      await api.delete(`/calls/${call.call_id}`);
      await fetchCalls();
    } catch {
      // ignore
    } finally {
      setHangingUp(null);
    }
  };

  const handleTrace = (call: ActiveCall) => {
    setTraceCallId(call.call_id);
    setTraceOpen(true);
  };

  const statChips = [
    { label: 'Total', value: stats.total, color: theme.palette.primary.main },
    { label: 'Ringing', value: stats.ringing, color: theme.palette.warning.main },
    { label: 'Connected', value: stats.connected, color: theme.palette.success.main },
    { label: 'On Hold', value: stats.on_hold, color: theme.palette.info.main },
  ];

  // Customer = the authoritative tenant resolved on each active call (not a DID guess).
  const UNATTR = '(unattributed)';
  const perCustomer = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of calls) { const k = c.tenant_name || UNATTR; m.set(k, (m.get(k) || 0) + 1); }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [calls]);
  const customerNames = useMemo(
    () => Array.from(new Set(calls.map((c) => c.tenant_name || UNATTR))).sort(),
    [calls],
  );
  const countryNames = useMemo(
    () => Array.from(new Set(calls.map((c) => countryOf(c.dst_number)))).sort(),
    [calls],
  );
  const visibleCalls = calls.filter(
    (c) => (!customerFilter || (c.tenant_name || UNATTR) === customerFilter)
        && (!countryFilter || countryOf(c.dst_number) === countryFilter),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
          Live Calls
        </Typography>
        {statChips.map((chip) => (
          <Chip
            key={chip.label}
            label={`${chip.label}: ${chip.value}`}
            size="small"
            sx={{
              bgcolor: alpha(chip.color, 0.12),
              color: chip.color,
              fontWeight: 600,
              border: `1px solid ${alpha(chip.color, 0.3)}`,
            }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <Select
            value={customerFilter}
            displayEmpty
            onChange={(e) => setCustomerFilter(e.target.value as string)}
            sx={{ height: 34 }}
          >
            <MenuItem value="">All customers ({calls.length})</MenuItem>
            {customerNames.map((n) => (
              <MenuItem key={n} value={n}>
                {n} ({perCustomer.find((p) => p[0] === n)?.[1] ?? 0})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select value={countryFilter} displayEmpty onChange={(e) => setCountryFilter(e.target.value as string)} sx={{ height: 34 }}>
            <MenuItem value="">All countries</MenuItem>
            {countryNames.map((n) => (
              <MenuItem key={n} value={n}>
                {n} ({calls.filter((c) => countryOf(c.dst_number) === n).length})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={autoRefresh ? 'Auto-refresh on (5s)' : 'Auto-refresh off'}>
            <Box
              onClick={() => setAutoRefresh((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                border: `1px solid ${autoRefresh ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.divider, 1)}`,
                bgcolor: autoRefresh ? alpha(theme.palette.success.main, 0.08) : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <DotIcon
                sx={{
                  fontSize: 10,
                  color: autoRefresh ? theme.palette.success.main : theme.palette.action.disabled,
                  animation: autoRefresh ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }}
              />
              <Typography variant="caption" sx={{ color: autoRefresh ? theme.palette.success.main : theme.palette.text.disabled, fontWeight: 500 }}>
                Live
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={monitorOpen ? 'Hide SBC Monitor' : 'Show SBC Monitor'}>
            <IconButton
              size="small"
              onClick={() => setMonitorOpen((v) => !v)}
              sx={{
                bgcolor: monitorOpen ? alpha('#00b0ff', 0.15) : 'transparent',
                border: `1px solid ${monitorOpen ? '#00b0ff' : theme.palette.divider}`,
                color: monitorOpen ? '#00b0ff' : theme.palette.text.secondary,
                '&:hover': { bgcolor: alpha('#00b0ff', 0.1), borderColor: '#00b0ff', color: '#00b0ff' },
              }}
            >
              <MonitorIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={fetchCalls}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {calls.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mr: 0.5, fontWeight: 600 }}>
            Live per customer:
          </Typography>
          <Chip
            label={`All: ${calls.length}`}
            size="small"
            onClick={() => setCustomerFilter('')}
            variant={customerFilter === '' ? 'filled' : 'outlined'}
            color={customerFilter === '' ? 'primary' : 'default'}
          />
          {perCustomer.map(([name, count]) => (
            <Chip
              key={name}
              label={`${name}: ${count}`}
              size="small"
              onClick={() => setCustomerFilter(customerFilter === name ? '' : name)}
              variant={customerFilter === name ? 'filled' : 'outlined'}
              color={customerFilter === name ? 'primary' : 'default'}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden' }}>
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            overflow: 'auto',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, fontWeight: 600 }}></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Carrier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SIP Call-ID</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCalls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: theme.palette.text.disabled }}>
                    {customerFilter ? `No live calls for ${customerFilter}` : 'No active calls'}
                  </TableCell>
                </TableRow>
              ) : (
                visibleCalls.map((call) => (
                  <TableRow key={call.id} hover>
                    <TableCell>
                      <Tooltip title={call.direction === 'inbound' ? 'Inbound' : 'Outbound'}>
                        {call.direction === 'inbound' ? (
                          <InboundIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                        ) : (
                          <OutboundIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{call.src_number}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{call.dst_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                        {call.carrier_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 140, fontWeight: 600, color: call.tenant_name ? 'inherit' : theme.palette.text.disabled }}>
                        {call.tenant_name || UNATTR}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={call.status.replace('_', ' ')}
                        size="small"
                        color={statusColors[call.status] ?? 'default'}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                      {formatDuration(call.start_time, call.answer_time)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={call.sip_call_id} placement="top">
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: theme.palette.text.secondary,
                            maxWidth: 140,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'default',
                          }}
                        >
                          {call.sip_call_id}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="SIP Trace">
                          <IconButton
                            size="small"
                            onClick={() => handleTrace(call)}
                            sx={{ color: '#00b0ff', '&:hover': { bgcolor: alpha('#00b0ff', 0.1) } }}
                          >
                            <TraceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hangup call">
                          <IconButton
                            size="small"
                            onClick={() => handleHangup(call)}
                            disabled={hangingUp === call.id}
                            sx={{ color: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                          >
                            {hangingUp === call.id ? (
                              <CircularProgress size={16} color="error" />
                            ) : (
                              <HangupIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {monitorOpen && <SBCMonitorPanel />}
      </Box>

      <SipTraceModal
        callId={traceCallId}
        open={traceOpen}
        onClose={() => {
          setTraceOpen(false);
          setTraceCallId(null);
        }}
      />

      <Box sx={{ display: 'none' }}>{tick}</Box>
    </Box>
  );
};
